import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { getMediaBlobUrl } from '../../utils/api';
import { 
  Newspaper, Search, Filter, Calendar, User, Tag, ChevronRight, Loader2, X, Eye
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import BackButton from '../../components/BackButton';

// Category colors
const CATEGORY_COLORS = {
  announcement: { bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  event: { bg: 'bg-purple-500', light: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  update: { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  news: { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
  default: { bg: 'bg-slate-500', light: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' }
};

const AuthenticatedImage = ({ src, alt, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadImage = async () => {
      if (src) {
        try {
          const url = await getMediaBlobUrl(src);
          if (mounted) { setImageUrl(url); setLoading(false); }
        } catch (e) {
          if (mounted) { setError(true); setLoading(false); }
        }
      } else { setLoading(false); }
    };
    loadImage();
    return () => { mounted = false; if (imageUrl) window.URL.revokeObjectURL(imageUrl); };
  }, [src]);

  if (loading) return <div className={`${className} bg-slate-200 animate-pulse flex items-center justify-center`}><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (error || !imageUrl) return <div className={`${className} bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center`}><Newspaper className="w-12 h-12 text-slate-300" /></div>;
  return <img src={imageUrl} alt={alt} className={className} />;
};

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingNews, setViewingNews] = useState(null);
  
  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => { loadNews(); }, []);

  const loadNews = async () => {
    try {
      const response = await api.get('/news');
      setNews(response.data);
    } catch (error) {
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  // Filtered news
  const filteredNews = useMemo(() => {
    return news.filter(n => {
      const matchesSearch = n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           n.content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || (n.category || 'news') === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [news, searchQuery, selectedCategory]);

  // Featured news (latest with image)
  const featuredNews = useMemo(() => {
    return filteredNews.find(n => n.image) || filteredNews[0];
  }, [filteredNews]);

  // Rest of news (excluding featured)
  const restNews = useMemo(() => {
    return filteredNews.filter(n => n.id !== featuredNews?.id);
  }, [filteredNews, featuredNews]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getCategoryColor = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS.default;

  return (
    <div className="space-y-6" data-testid="member-news-page">
      <BackButton to="/member" />
      
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="bg-gradient-to-br from-orange-500 to-rose-600 p-4 rounded-2xl shadow-lg">
          <Newspaper className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-serif font-bold text-slate-900">News & Announcements</h1>
          <p className="text-slate-600">Latest updates from AHSGA</p>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search news..." className="pl-10 border-slate-200" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-slate-700">
            <option value="all">All Categories</option>
            <option value="news">News</option>
            <option value="announcement">Announcements</option>
            <option value="event">Events</option>
            <option value="update">Updates</option>
          </select>
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2">
        {['all', 'news', 'announcement', 'event', 'update'].map(cat => {
          const color = cat === 'all' ? { bg: 'bg-slate-500' } : getCategoryColor(cat);
          const isActive = selectedCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all capitalize ${
                isActive 
                  ? `${color.bg} text-white shadow-lg` 
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              {cat === 'all' ? 'All' : cat}
            </button>
          );
        })}
      </div>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl bg-white text-slate-900 border-slate-200 max-h-[90vh] overflow-y-auto">
          {viewingNews && (
            <>
              <DialogHeader><DialogTitle className="text-slate-900 text-2xl font-serif">{viewingNews.title}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {viewingNews.image && <AuthenticatedImage src={viewingNews.image} alt={viewingNews.title} className="w-full h-64 object-cover rounded-xl" />}
                <div className="flex items-center space-x-4 text-sm text-slate-500">
                  <span className="flex items-center"><User className="w-4 h-4 mr-1" />{viewingNews.author}</span>
                  <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" />{formatDate(viewingNews.published_at)}</span>
                  <span className={`${getCategoryColor(viewingNews.category || 'news').bg} text-white text-xs px-2 py-1 rounded-full capitalize`}>{viewingNews.category || 'news'}</span>
                </div>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{viewingNews.content}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* News Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
      ) : filteredNews.length > 0 ? (
        <div className="space-y-8">
          {/* Featured Article */}
          {featuredNews && (
            <article 
              className="group relative bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-orange-300 hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => { setViewingNews(featuredNews); setViewDialogOpen(true); }}
            >
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <AuthenticatedImage src={featuredNews.image} alt={featuredNews.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent md:hidden" />
                  <div className="absolute top-4 left-4">
                    <span className={`${getCategoryColor(featuredNews.category || 'news').bg} text-white text-xs px-3 py-1 rounded-full font-medium capitalize shadow-lg`}>
                      {featuredNews.category || 'news'}
                    </span>
                  </div>
                </div>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <div className="flex items-center space-x-3 text-xs text-slate-500 mb-3">
                    <span className="flex items-center"><User className="w-3 h-3 mr-1" />{featuredNews.author}</span>
                    <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{formatDate(featuredNews.published_at)}</span>
                  </div>
                  <h2 className="font-serif font-bold text-2xl md:text-3xl text-slate-900 mb-4 group-hover:text-orange-600 transition-colors line-clamp-2">{featuredNews.title}</h2>
                  <p className="text-slate-600 line-clamp-4 mb-6">{featuredNews.content}</p>
                  <div className="flex items-center text-orange-500 font-medium">
                    Read Full Article <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </article>
          )}

          {/* Rest of News Grid */}
          {restNews.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {restNews.map((item) => {
                const catColor = getCategoryColor(item.category || 'news');
                return (
                  <article 
                    key={item.id} 
                    className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-orange-300 hover:shadow-xl transition-all duration-300 cursor-pointer"
                    onClick={() => { setViewingNews(item); setViewDialogOpen(true); }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <AuthenticatedImage src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-3 left-3">
                        <span className={`${catColor.bg} text-white text-xs px-2 py-1 rounded-full font-medium capitalize shadow-md`}>{item.category || 'news'}</span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center space-x-3 text-xs text-slate-500 mb-2">
                        <span>{item.author}</span>
                        <span>•</span>
                        <span>{formatDate(item.published_at)}</span>
                      </div>
                      <h3 className="font-semibold text-lg text-slate-900 mb-2 line-clamp-2 group-hover:text-orange-600 transition-colors">{item.title}</h3>
                      <p className="text-sm text-slate-600 line-clamp-3 mb-4">{item.content}</p>
                      <div className="flex items-center text-orange-500 text-sm font-medium">
                        Read more <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl text-center py-16">
          <Newspaper className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-600 text-lg">No news articles found</p>
          <p className="text-sm text-slate-500 mt-2">{searchQuery || selectedCategory !== 'all' ? 'Try adjusting your filters' : 'Check back later for updates'}</p>
        </div>
      )}
    </div>
  );
};

export default News;
